import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Crown, Loader2, Sparkles } from "lucide-react";
import { api, inr, apiError } from "@/lib/api";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function RestaurantSubscriptions() {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/restaurant/subscription").then((r) => setData(r.data)).catch(() => {});
    api.get("/subscription/plans").then((r) => setPlans(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const startTrial = async (p) => {
    setBusy(p.id);
    try { await api.post("/restaurant/subscription/start-trial", { plan_id: p.id, cycle }); toast.success(`${p.name} trial started`); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel subscription? Access continues until the renewal date.")) return;
    try { const { data: d } = await api.post("/restaurant/subscription/cancel"); toast.success(`Cancelled. Access until ${d.access_until?.slice(0, 10)}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const subscribe = async (p) => {
    setBusy(p.id);
    try {
      const { data: res } = await api.post("/restaurant/subscription/subscribe", { plan_id: p.id, cycle });
      if (res.activated) { toast.success(`${p.name} activated`); load(); return; }
      if (res.gateway === "razorpay") {
        const ok = await loadRazorpay();
        if (!ok) return toast.error("Could not load payment gateway");
        const rzp = new window.Razorpay({
          key: res.key_id, amount: res.amount, currency: res.currency, name: "BiteGoFood", description: `${p.name} subscription`,
          order_id: res.razorpay_order_id,
          handler: async (resp) => {
            try {
              await api.post("/restaurant/subscription/verify", { subscription_id: res.subscription_id, razorpay_order_id: resp.razorpay_order_id, razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature });
              toast.success(`${p.name} activated`); load();
            } catch (e) { toast.error(apiError(e)); }
          },
          theme: { color: "#FF6B35" },
        });
        rzp.open();
      } else { toast.success("Subscription created (mock)"); load(); }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (!data) return <div className="p-8 text-center text-ink-600">Loading…</div>;
  const cur = data.subscription;
  const curPlanId = data.plan?.id;
  const usage = data.usage || {};
  const limit = data.plan?.limits || {};

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="bg-gradient-to-r from-ink to-slate-700 text-white rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs text-white/70">Current plan</div>
          <div className="font-display font-bold text-2xl flex items-center gap-2"><Crown size={22} className="text-secondary" /> {data.plan?.name || "BiteGoFood Starter"}</div>
          {cur && <div className="text-sm text-white/80 mt-0.5 capitalize">{cur.status}{cur.renewal_date ? ` · renews ${cur.renewal_date.slice(0, 10)}` : ""}</div>}
        </div>
        {cur && ["active", "trial"].includes(cur.status) && cur.plan_key !== "starter" && <button onClick={cancel} className="bg-white/15 hover:bg-white/25 rounded-xl px-4 py-2 text-sm font-semibold" data-testid="cancel-sub">Cancel</button>}
      </div>

      <div className="bg-white rounded-2xl border border-border p-4">
        <h3 className="font-bold text-ink mb-2">Plan usage</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          {[["Menu items", usage.menu_items, limit.menu_items], ["Campaigns", usage.campaigns, limit.campaigns], ["Outlets", usage.outlets, limit.outlets]].map(([l, used, max]) => (
            <div key={l} className="bg-surface rounded-xl p-3">
              <div className="flex justify-between text-sm"><span className="text-ink-600">{l}</span><span className="font-bold">{used} / {max === -1 || max == null ? "∞" : max}</span></div>
              {max > 0 && <div className="h-1.5 bg-border rounded-full mt-2 overflow-hidden"><div className="h-full bg-coral" style={{ width: `${Math.min(100, (used / max) * 100)}%` }} /></div>}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center gap-2">
        <span className={`text-sm font-semibold ${cycle === "monthly" ? "text-coral" : "text-ink-600"}`}>Monthly</span>
        <button onClick={() => setCycle((c) => c === "monthly" ? "yearly" : "monthly")} className="w-12 h-6 rounded-full bg-coral relative" data-testid="cycle-toggle"><span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${cycle === "yearly" ? "left-6" : "left-0.5"}`} /></button>
        <span className={`text-sm font-semibold ${cycle === "yearly" ? "text-coral" : "text-ink-600"}`}>Yearly <span className="text-veg text-xs">(save ~17%)</span></span>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        {plans.map((p) => {
          const price = cycle === "yearly" ? p.price_yearly : p.price_monthly;
          const isCurrent = p.id === curPlanId && cur && ["active", "trial"].includes(cur.status);
          const canTrial = p.trial_days > 0 && !isCurrent;
          return (
            <div key={p.id} className={`rounded-2xl border p-4 flex flex-col ${isCurrent ? "border-coral ring-2 ring-coral/30" : "border-border"} bg-white`} data-testid={`plan-${p.key}`}>
              <div className="flex items-center gap-1.5">{p.key === "pro" && <Sparkles size={15} className="text-coral" />}<h4 className="font-display font-bold text-ink">{p.name}</h4></div>
              <div className="mt-1"><span className="font-extrabold text-2xl text-ink">{price === 0 ? "Free" : inr(price)}</span>{price > 0 && <span className="text-xs text-ink-600">/{cycle === "yearly" ? "yr" : "mo"}</span>}</div>
              <p className="text-xs text-ink-600 mt-1 mb-2">{p.description}</p>
              <ul className="space-y-1 flex-1">
                {p.features.slice(0, 6).map((f, i) => <li key={i} className="flex items-start gap-1.5 text-xs text-ink"><Check size={13} className="text-veg mt-0.5 shrink-0" /> {f}</li>)}
              </ul>
              <div className="mt-3 space-y-2">
                {isCurrent ? <div className="text-center bg-coral-light text-coral font-bold text-sm py-2 rounded-xl">Current plan</div> : (
                  <>
                    <button onClick={() => subscribe(p)} disabled={busy === p.id} className="w-full bg-coral text-white font-bold text-sm py-2 rounded-xl tap flex items-center justify-center gap-1.5" data-testid={`subscribe-${p.key}`}>{busy === p.id ? <Loader2 size={15} className="animate-spin" /> : (price === 0 ? "Switch to Free" : "Subscribe")}</button>
                    {canTrial && <button onClick={() => startTrial(p)} disabled={busy === p.id} className="w-full border border-coral text-coral font-bold text-sm py-2 rounded-xl tap" data-testid={`trial-${p.key}`}>Start {p.trial_days}-day trial</button>}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {data.invoices?.length > 0 && (
        <div className="bg-white rounded-2xl border border-border p-4">
          <h3 className="font-bold text-ink mb-2">Billing history</h3>
          <table className="w-full text-sm"><thead><tr className="text-left text-xs text-ink-600 uppercase border-b border-border">{["Invoice", "Plan", "Amount", "Status", "Date"].map((c) => <th key={c} className="py-2">{c}</th>)}</tr></thead>
            <tbody>{data.invoices.map((iv) => <tr key={iv.id} className="border-b border-border last:border-0"><td className="py-2 font-semibold">{iv.code}</td><td>{iv.plan_name}</td><td>{inr(iv.amount)}</td><td className="capitalize text-veg">{iv.status}</td><td>{iv.created_at?.slice(0, 10)}</td></tr>)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
